import * as Joi from 'joi';
import { Moment } from 'moment';
import {
	Column,
	Entity,
	JoinColumn,
	JoinTable,
	ManyToMany,
	ManyToOne,
	OneToMany,
	OneToOne,
	PrimaryGeneratedColumn,
} from 'typeorm';
import { booleanTransformer, GenPartial, momentTransformer } from 'typeormgen';
import Account from '../accounts/account.entity';
import Asset from '../assets/asset.entity';
import Base from '../base.entity';
import DepartmentSetting from '../department-settings/department-setting.entity';
import { SCRIPT_OVERRIDES } from '../enums/enums';
import EpisodicCharacter from '../episodic-characters/episodic-character.entity';
import EpisodicItem from '../episodic-items/episodic-item.entity';
import EpisodicLogoPicture from '../episodic-logo-pictures/episodic-logo-picture.entity';
import EpisodicProp from '../episodic-props/episodic-prop.entity';
import Permission from '../permissions/permission.entity';
import Picture from '../pictures/picture.entity';
import Production from '../productions/production.entity';
import Series from '../series/series.entity';
import SetPiece from '../set-pieces/set-piece.entity';
import Set from '../sets/set.entity';
import Vendor from '../vendors/vendor.entity';

@Entity('episodics')
export default class Episodic extends Base {
	static tableName = 'episodics';
	static readonly skipUpdateColumns = [
		'departmentSettings',
		'is_feature',
		'productions',
		'shoot_method',
	];

	@PrimaryGeneratedColumn()
	id: number;

	@Column('int')
	series_id: number;

	@Column('int')
	account_id: number;

	@Column({ type: 'int', transformer: booleanTransformer })
	disqualified: boolean;

	@Column({ type: 'int', transformer: booleanTransformer })
	is_feature: boolean;

	@Column({ type: 'int', transformer: booleanTransformer })
	shoot_method: boolean;

	@Column({ type: 'int', transformer: booleanTransformer })
	sort_alpha: boolean;

	@Column({ type: 'int', transformer: booleanTransformer })
	read_only: boolean;

	@Column('varchar')
	series_title: string;

	@Column('varchar')
	season: string;

	@Column({ type: 'int', transformer: booleanTransformer })
	script_upload_disabled: boolean;

	@Column('int')
	script_override_type: SCRIPT_OVERRIDES;

	@Column({ type: 'int', transformer: booleanTransformer })
	watermark: boolean;

	@Column({ type: 'int', transformer: booleanTransformer })
	mobile_only: boolean;

	@Column({ type: 'int', transformer: booleanTransformer })
	require_2fa: boolean;

	@Column('int')
	session_limit: number;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	show_lite_plan: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	security_enabled: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	screenshot_enabled: boolean;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	last_updated_time: Moment;

	@Column('int')
	last_updated_user: number;

	@Column('int')
	created_by: number;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	date_created: Moment;

	@Column('int')
	prod_owner: number;

	@ManyToOne((type) => Account, (account) => account.episodics)
	@JoinColumn({
		name: 'account_id',
	})
	account: Account;

	@OneToMany(
		(type) => DepartmentSetting,
		(departmentSetting) => departmentSetting.episodic
	)
	departmentSettings: DepartmentSetting[];

	@OneToMany((type) => Production, (production) => production.episodic)
	productions: Production[];

	@OneToMany(
		(type) => EpisodicCharacter,
		(episodicCharacter) => episodicCharacter.episodic
	)
	episodicCharacters: EpisodicCharacter[];

	@OneToMany((type) => EpisodicItem, (episodicItem) => episodicItem.episodic)
	episodicItems: EpisodicItem[];

	@OneToMany((type) => EpisodicProp, (episodicProp) => episodicProp.episodic)
	episodicProps: EpisodicProp[];

	@OneToMany((type) => Set, (set) => set.episodic)
	sets: Set[];

	@OneToMany((type) => SetPiece, (setPiece) => setPiece.episodic)
	setPieces: SetPiece[];

	@OneToMany((type) => Asset, (asset) => asset.episodic)
	assets: Asset[];

	@OneToMany((type) => Vendor, (vendor) => vendor.episodic)
	vendors: Vendor[];

	@OneToMany((type) => Permission, (permission) => permission.episodic)
	permissions: Permission[];

	@ManyToOne((type) => Series, (series) => series.episodics)
	@JoinColumn({
		name: 'series_id',
	})
	series: Series;

	@OneToOne(
		(type) => EpisodicLogoPicture,
		(episodicLogoPicture) => episodicLogoPicture.episodic
	)
	episodicLogoPicture: EpisodicLogoPicture;

	@ManyToMany((type) => Picture, (pic) => pic.episodicLogoPicture)
	@JoinTable({
		name: 'episodic_logo_pictures',
		joinColumn: { name: 'episodic_id' },
		inverseJoinColumn: { name: 'picture_id' },
	})
	// should only ever have one
	logo: Picture[];

	static rules() {
		return {
			id: Joi.number().integer(),
			series_id: Joi.number().integer().allow(null),
			account_id: Joi.number().integer().allow(null),
			disqualified: Joi.boolean(),
			is_feature: Joi.boolean(),
			shoot_method: Joi.boolean(),
			sort_alpha: Joi.boolean(),
			read_only: Joi.boolean(),
			series_title: Joi.string().max(300).allow(null),
			season: Joi.string().max(50).allow(null, ''),
			script_upload_disabled: Joi.boolean(),
			script_override_type: Joi.number().integer(),
			watermark: Joi.boolean(),
			mobile_only: Joi.boolean(),
			require_2fa: Joi.boolean(),
			session_limit: Joi.number().integer(),
			show_lite_plan: Joi.boolean().allow(null),
			security_enabled: Joi.boolean().allow(null),
			last_updated_time: Joi.date().allow(null),
			last_updated_user: Joi.number().integer().allow(null),
			created_by: Joi.number().integer().allow(null),
			date_created: Joi.date().allow(null),
			prod_owner: Joi.number().integer().allow(null),
			productions: Joi.array().allow(null),
			departmentSettings: Joi.array().allow(null),
			logo: Joi.array().allow(null),
			screenshot_enabled: Joi.boolean().allow(null),
		};
	}

	baseToJSON() {
		return {
			id: this.id,
			series_id: this.series_id,
			account_id: this.account_id,
			disqualified: this.disqualified,
			is_feature: this.is_feature,
			shoot_method: this.shoot_method,
			sort_alpha: this.sort_alpha,
			read_only: this.read_only,
			series_title: this.series_title,
			season: this.season,
			script_upload_disabled: this.script_upload_disabled,
			script_override_type: this.script_override_type,
			watermark: this.watermark,
			mobile_only: this.mobile_only,
			require_2fa: this.require_2fa,
			session_limit: this.session_limit,
			show_lite_plan: this.show_lite_plan,
			security_enabled: this.security_enabled,
			last_updated_time: this.last_updated_time,
			last_updated_user: this.last_updated_user,
			created_by: this.created_by,
			date_created: this.date_created,
			prod_owner: this.prod_owner,
			screenshot_enabled: this.screenshot_enabled,
		};
	}

	toJSON() {
		return {
			...this.baseToJSON(),
			departmentSettings: this.departmentSettings,
			productions: this.productions,
			logo: this.logo,
		};
	}

	assign(props?: GenPartial<Episodic>) {
		super.assign(props);

		if (props) {
			if (typeof props.disqualified !== 'undefined') {
				this.disqualified = booleanTransformer.from(props.disqualified);
			}
			if (typeof props.is_feature !== 'undefined') {
				this.is_feature = booleanTransformer.from(props.is_feature);
			}
			if (typeof props.shoot_method !== 'undefined') {
				this.shoot_method = booleanTransformer.from(props.shoot_method);
			}
			if (typeof props.sort_alpha !== 'undefined') {
				this.sort_alpha = booleanTransformer.from(props.sort_alpha);
			}
			if (typeof props.read_only !== 'undefined') {
				this.read_only = booleanTransformer.from(props.read_only);
			}
			if (typeof props.watermark !== 'undefined') {
				this.watermark = booleanTransformer.from(props.watermark);
			}
			if (typeof props.mobile_only !== 'undefined') {
				this.mobile_only = booleanTransformer.from(props.mobile_only);
			}
			if (typeof props.require_2fa !== 'undefined') {
				this.require_2fa = booleanTransformer.from(props.require_2fa);
			}
			if (typeof props.show_lite_plan !== 'undefined') {
				this.show_lite_plan = booleanTransformer.from(props.show_lite_plan);
			}
			if (typeof props.security_enabled !== 'undefined') {
				this.security_enabled = booleanTransformer.from(props.security_enabled);
			}
			if (typeof props.screenshot_enabled !== 'undefined') {
				this.screenshot_enabled = booleanTransformer.from(
					props.screenshot_enabled
				);
			}
			if (typeof props.last_updated_time !== 'undefined') {
				this.last_updated_time = momentTransformer.from(
					props.last_updated_time
				);
			}
			if (typeof props.date_created !== 'undefined') {
				this.date_created = momentTransformer.from(props.date_created);
			}
		}
	}

	constructor(props?: GenPartial<Episodic>) {
		super(props);
	}

	getTagName() {
		return this.series_title;
	}

	getShootTypeString() {
		if (this.shoot_method) {
			return 'Episodic as feature';
		} else if (this.is_feature) {
			return 'Feature';
		} else {
			return 'Episodic';
		}
	}

	isLocked(): boolean {
		return this.read_only;
	}

	isFeature(): boolean {
		return this.is_feature && !this.shoot_method;
	}

	isLikeAFeature(): boolean {
		return this.is_feature && this.shoot_method;
	}

	isTraditionalTV(): boolean {
		return !this.is_feature && !this.shoot_method;
	}

	isLockedOrExpired(deptSettings: DepartmentSetting[]): boolean {
		if (this.read_only) {
			return true;
		}

		for (const ds of deptSettings) {
			if (!ds.isExpired()) {
				return false;
			}
		}

		return true;
	}
}
